
import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BIOMARKER_PAGE_PATH } from '@/data/biomarkerPlatformSeo';
import { PNP_PAGE_PATH } from '@/data/personalisedNutritionSeo';

export interface NavSection {
  href: string;
  label: string;
}

interface NavLinksProps {
  isLandingPage: boolean;
  onOpenRegisterDialog?: () => void;
  activeSection?: string;
  sections?: NavSection[];
  showQuickLinks?: boolean;
  density?: 'default' | 'compact';
  /** Scroll to in-page sections without writing location.hash (used by /start). */
  scrollWithoutHash?: boolean;
}

export const QUICK_LINKS: NavSection[] = [
  {
    href: PNP_PAGE_PATH,
    label: 'Personalised Nutrition Platform',
  },
  {
    href: BIOMARKER_PAGE_PATH,
    label: 'Biomarker-Informed Nutrition',
  },
];

const defaultLinkClass =
  'px-5 text-[1rem] font-medium leading-6 tracking-[0.05em] text-gray-700 transition-colors hover:text-cs-primary';

const compactLinkClass =
  'px-2 lg:px-2.5 xl:px-3 2xl:px-5 text-[0.8125rem] lg:text-[0.875rem] 2xl:text-[1rem] font-medium leading-6 tracking-[0.02em] 2xl:tracking-[0.05em] text-gray-700 transition-colors hover:text-cs-primary whitespace-nowrap';

const LANDING_NAV_SECTIONS: NavSection[] = [
  { href: '#top', label: 'Home' },
  { href: '#about-us', label: 'About Us' },
  { href: '#features', label: 'Features' },
  { href: '#why-we-exist', label: 'Vision & Mission' },
  { href: '#pricing', label: 'Pricing' },
  { href: '#blogs', label: 'Blogs' },
];

function analyticsIdFromHref(href: string): string {
  const hashIndex = href.indexOf('#');
  const rawId = hashIndex >= 0 ? href.slice(hashIndex + 1) : href;
  return rawId.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '').toLowerCase() || 'link';
}

const scrollToHashTarget = (href: string) => {
  const hashIndex = href.indexOf('#');
  if (hashIndex < 0) return;
  const id = href.slice(hashIndex + 1);
  if (!id) return;
  const el = document.getElementById(id);
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const NavLinks: React.FC<NavLinksProps> = ({
  isLandingPage,
  activeSection = 'home',
  sections,
  showQuickLinks = isLandingPage && !sections,
  density = 'default',
  scrollWithoutHash = false,
}) => {
  const items = sections ?? (isLandingPage ? LANDING_NAV_SECTIONS : null);
  if (!items) return null;

  const linkClass = density === 'compact' ? compactLinkClass : defaultLinkClass;

  return (
    <>
      {items.map(({ href, label }) => {
        const sectionId = analyticsIdFromHref(href);
        const isActive =
          activeSection === sectionId || (activeSection === 'home' && sectionId === 'top');
        const isHashLink = href.startsWith('#');

        if (scrollWithoutHash && isHashLink) {
          return (
            <button
              key={href}
              type="button"
              className={linkClass}
              style={isActive ? { color: '#58D4F8' } : undefined}
              onClick={() => scrollToHashTarget(href)}
              data-analytics-event="Landing CTA Clicked"
              data-analytics-id={`navlink_${sectionId}`}
              data-analytics-label={label}
              data-analytics-section="navbar_links"
            >
              {label}
            </button>
          );
        }

        return (
          <a
            key={href}
            href={href}
            className={linkClass}
            style={isActive ? { color: '#58D4F8' } : undefined}
            data-analytics-event="Landing CTA Clicked"
            data-analytics-id={`navlink_${sectionId}`}
            data-analytics-label={label}
            data-analytics-section="navbar_links"
          >
            {label}
          </a>
        );
      })}
      {showQuickLinks ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={`${linkClass} inline-flex items-center gap-1.5 outline-none focus-visible:text-cs-primary`}
            data-analytics-event="Landing CTA Clicked"
            data-analytics-id="navlink_quick_links"
            data-analytics-label="Quick Links"
            data-analytics-section="navbar_links"
          >
            Quick Links
            <ChevronDown className="h-4 w-4" aria-hidden />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={12}
            className="w-72 rounded-xl border border-[#E7EEF2] bg-white p-2 shadow-[0_1rem_2rem_rgba(15,23,42,0.12)]"
          >
            {QUICK_LINKS.map(({ href, label }) => (
              <DropdownMenuItem key={href} asChild className="rounded-lg p-0 focus:bg-[#F1F8FE]">
                <a
                  href={href}
                  className="block w-full px-3 py-2.5 text-[0.9375rem] font-medium leading-5 text-gray-700 hover:text-cs-primary"
                  data-analytics-event="Landing CTA Clicked"
                  data-analytics-id={`navlink_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`}
                  data-analytics-label={label}
                  data-analytics-section="navbar_quick_links"
                >
                  {label}
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </>
  );
};

export default NavLinks;
